/**
 * ParticleSystem.ts — Lightweight canvas-based particle engine.
 *
 * Renders ambient particles over the terminal for atmospheric effects.
 * Uses a pre-allocated particle pool (no GC pressure), throttled to ~30fps.
 * Canvas is positioned absolutely over the terminal with pointer-events: none.
 *
 * Ported from RotaryKnights — presets replaced with Gallowcrest-specific:
 * snow (outdoor default), embers (combat/fire), arcane (magic), mist (mystery).
 */

// ─── Types ──────────────────────────────────────────────────────

export type ParticlePreset = 'snow' | 'embers' | 'arcane' | 'mist' | 'none';

interface Particle {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  alpha: number;
  alphaDecay: number;
  size: number;
  color: string;
  life: number;
  maxLife: number;
}

interface PresetConfig {
  spawnRate: number;        // particles per frame
  colors: string[];
  sizeMin: number;
  sizeMax: number;
  alphaMin: number;
  alphaMax: number;
  alphaDecay: number;
  vxRange: [number, number];
  vyRange: [number, number];
  lifeMin: number;
  lifeMax: number;
}

// ─── Preset Configurations ──────────────────────────────────────

const PRESETS: Record<Exclude<ParticlePreset, 'none'>, PresetConfig> = {
  snow: {
    spawnRate: 2,
    colors: ['#e0e8f0', '#c0d0e0', '#ffffff', '#a8c8e8'],
    sizeMin: 1,
    sizeMax: 2.5,
    alphaMin: 0.2,
    alphaMax: 0.5,
    alphaDecay: 0.002,
    vxRange: [-0.5, 0.5],
    vyRange: [0.5, 1.5],
    lifeMin: 150,
    lifeMax: 300,
  },
  embers: {
    spawnRate: 3,
    colors: ['#ff6600', '#ff3300', '#ff9944', '#ffcc00'],
    sizeMin: 1,
    sizeMax: 2,
    alphaMin: 0.4,
    alphaMax: 0.8,
    alphaDecay: 0.008,
    vxRange: [-0.8, 0.8],
    vyRange: [-2.5, -0.5],
    lifeMin: 40,
    lifeMax: 100,
  },
  arcane: {
    spawnRate: 1,
    colors: ['#cc66ff', '#9933ff', '#ff66cc', '#6633ff'],
    sizeMin: 1,
    sizeMax: 3,
    alphaMin: 0.15,
    alphaMax: 0.45,
    alphaDecay: 0.003,
    vxRange: [-0.3, 0.3],
    vyRange: [-0.3, 0.3],
    lifeMin: 100,
    lifeMax: 200,
  },
  mist: {
    spawnRate: 1,
    colors: ['#8090a0', '#708090', '#607080', '#90a0b0'],
    sizeMin: 2,
    sizeMax: 4,
    alphaMin: 0.08,
    alphaMax: 0.2,
    alphaDecay: 0.001,
    vxRange: [0.2, 0.8],
    vyRange: [-0.1, 0.1],
    lifeMin: 200,
    lifeMax: 400,
  },
};

// ─── Constants ──────────────────────────────────────────────────

const MAX_PARTICLES = 150;
const TARGET_FPS = 30;
const FRAME_INTERVAL = 1000 / TARGET_FPS;

// ─── ParticleSystem ─────────────────────────────────────────────

export class ParticleSystem {
  private container: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private pool: Particle[];
  private preset: ParticlePreset = 'snow';
  private enabled = false;
  private animFrameId = 0;
  private lastFrameTime = 0;
  private resizeObserver: ResizeObserver;
  private boundOnVisibility: () => void;

  constructor(container: HTMLElement) {
    this.container = container;

    // Create overlay canvas
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:-1;';
    this.ctx = this.canvas.getContext('2d')!;

    // Pre-allocate particle pool
    this.pool = Array.from({ length: MAX_PARTICLES }, () => this.createParticle());

    // Handle container resize
    this.resizeObserver = new ResizeObserver(() => this.syncSize());
    this.resizeObserver.observe(container);

    // Auto-pause on tab hidden
    this.boundOnVisibility = () => {
      if (document.hidden) {
        this.stopLoop();
      } else if (this.enabled) {
        this.startLoop();
      }
    };
    document.addEventListener('visibilitychange', this.boundOnVisibility);
  }

  // ─── Public API ─────────────────────────────────────────────

  /** Enable the particle system. */
  enable(): void {
    if (this.enabled) return;
    this.enabled = true;
    if (!this.canvas.parentElement) {
      this.container.appendChild(this.canvas);
    }
    this.syncSize();
    this.startLoop();
    // Reassert z-index after paint to prevent race with terminal-container
    requestAnimationFrame(() => {
      this.canvas.style.zIndex = '-1';
    });
  }

  /** Disable the particle system. */
  disable(): void {
    if (!this.enabled) return;
    this.enabled = false;
    this.stopLoop();
    this.clearAll();
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (this.canvas.parentElement) {
      this.canvas.parentElement.removeChild(this.canvas);
    }
  }

  /** Check if particles are active. */
  isEnabled(): boolean {
    return this.enabled;
  }

  /** Set the active particle preset. 'none' stops spawning new particles. */
  setPreset(name: ParticlePreset): void {
    this.preset = name;
  }

  /** Get the current preset name. */
  getPreset(): ParticlePreset {
    return this.preset;
  }

  /** Clean up all resources. */
  destroy(): void {
    this.disable();
    this.resizeObserver.disconnect();
    document.removeEventListener('visibilitychange', this.boundOnVisibility);
  }

  // ─── Internal ───────────────────────────────────────────────

  private createParticle(): Particle {
    return {
      active: false, x: 0, y: 0, vx: 0, vy: 0,
      alpha: 0, alphaDecay: 0, size: 1, color: '#fff',
      life: 0, maxLife: 0,
    };
  }

  private syncSize(): void {
    const rect = this.container.getBoundingClientRect();
    this.canvas.width = rect.width;
    this.canvas.height = rect.height;
  }

  private startLoop(): void {
    if (this.animFrameId) return;
    this.lastFrameTime = performance.now();
    const loop = (now: number) => {
      this.animFrameId = requestAnimationFrame(loop);
      const delta = now - this.lastFrameTime;
      if (delta < FRAME_INTERVAL) return;
      this.lastFrameTime = now - (delta % FRAME_INTERVAL);
      this.update();
      this.render();
    };
    this.animFrameId = requestAnimationFrame(loop);
  }

  private stopLoop(): void {
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = 0;
    }
  }

  private clearAll(): void {
    for (const p of this.pool) p.active = false;
  }

  private spawn(): void {
    if (this.preset === 'none') return;
    const cfg = PRESETS[this.preset];
    const w = this.canvas.width;
    const h = this.canvas.height;
    if (w === 0 || h === 0) return;

    for (let i = 0; i < cfg.spawnRate; i++) {
      const p = this.pool.find(p => !p.active);
      if (!p) break; // pool exhausted

      p.active = true;
      p.color = cfg.colors[Math.floor(Math.random() * cfg.colors.length)];
      p.size = cfg.sizeMin + Math.random() * (cfg.sizeMax - cfg.sizeMin);
      p.alpha = cfg.alphaMin + Math.random() * (cfg.alphaMax - cfg.alphaMin);
      p.alphaDecay = cfg.alphaDecay;
      p.vx = cfg.vxRange[0] + Math.random() * (cfg.vxRange[1] - cfg.vxRange[0]);
      p.vy = cfg.vyRange[0] + Math.random() * (cfg.vyRange[1] - cfg.vyRange[0]);
      p.life = 0;
      p.maxLife = cfg.lifeMin + Math.random() * (cfg.lifeMax - cfg.lifeMin);

      // Spawn position depends on preset
      if (this.preset === 'snow') {
        p.x = Math.random() * w;
        p.y = -5;
      } else if (this.preset === 'embers') {
        p.x = Math.random() * w;
        p.y = h + 5;
      } else if (this.preset === 'mist') {
        p.x = -10;
        p.y = Math.random() * h;
      } else {
        // arcane: random position
        p.x = Math.random() * w;
        p.y = Math.random() * h;
      }
    }
  }

  private update(): void {
    this.spawn();
    const w = this.canvas.width;
    const h = this.canvas.height;
    for (const p of this.pool) {
      if (!p.active) continue;
      p.x += p.vx;
      p.y += p.vy;
      p.life++;
      p.alpha -= p.alphaDecay;

      // Kill if faded out, expired, or out of bounds
      if (p.alpha <= 0 || p.life >= p.maxLife ||
          p.x < -10 || p.x > w + 10 || p.y < -10 || p.y > h + 10) {
        p.active = false;
      }
    }
  }

  private render(): void {
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;
    ctx.clearRect(0, 0, w, h);

    for (const p of this.pool) {
      if (!p.active) continue;
      ctx.globalAlpha = Math.max(0, p.alpha);
      ctx.fillStyle = p.color;

      // All presets use circles
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }
}
